import {
  createAssets,
  createCreative,
  deleteAd,
  editCreative,
  getAssetDetails,
  getAssets,
  getBwCreativeAsset,
  getBwCreativeDetails,
  getCreativeAddons,
  getCreativeApprovals,
  getImageByUrn,
  getVisitorInsightAudience,
  refreshAds,
} from "@/apis/v1/assets";
import { useSnackbarDispatch } from "@/context/SnackbarContext";
import { messages } from "@/messages";
import { filterDataAccordingToAllowedAdvertisers } from "@/modules/Advertiser/advertiser.validation";
import getDimensionsFromImageDataUrl from "@/utils/getDimensionsFromImageDataUrl";
import { getVideoDuration } from "@/utils/helpers";
import {
  createBulkLinkedinAssets,
  createCleanZipFromValidEntries,
  getAssetInfo,
  validateAndCategorizeAssetsForDisplay,
  validateAssetFunctionByChannel,
  ZipEntryAssetInfo,
} from "@/utils/unzipFileUtils";
import { cleanFileName } from "@/constraints";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  CreativeType,
  DISPLAY_ASSET_AD_SIZE,
  displayAdSizes,
  internalCreativeTypeToLinkedinCreativeTypeMapping,
  linkedinAdSizes,
} from "./CreateOrEditAsset/data";
import { ASSET_TYPES } from "./data";
import useImageLoaded from "@/hooks/useImageLoaded";
import { AllowedCreativeType } from "./CreateOrEditAsset/types";
import { Channel, ImageLoadingState, ReactQueryOptions } from "@/types";
import { convertImageToBase64 } from "@/utils/convertFileToBytes";
import { v4 as uuidv4 } from "uuid";
import type {
  CreativeApiResponse,
  SingleCreative,
  ILoadBWAsset,
  ShowZipPreviewCallback,
} from "./asset.interface";
import routes from "@/routes";
import correctTypedErrorMessage from "@/utils/correctTypedErrorMessage";
import { enqueueSnackbar } from "notistack";
import { UseFormSetValue } from "react-hook-form";

/**
 * Sentinel error message for an intentional ZIP-preview cancellation. Shared
 * between the submit handler (which throws it) and this hook (which swallows
 * it without showing an error) so the two can't drift apart.
 */
export const UPLOAD_CANCELLED_ERROR = "Upload cancelled";

type Type = Channel | "ALL";
export const LIST_AD_CREATIVE = (
  type: Type,
  advertiserId?: number | null,
  page?: number,
  size?: number,
  adType?: number,
  isGrouped = false
) => ["list", "creative", isGrouped, type, advertiserId, page, size, adType];
export const LIST_AD_CREATIVE_DETAILS = (id: number, type: Channel) => [
  "list",
  "creative",
  id,
  type,
];
export const AD_ASSET = (id: number) => ["creatives", "bw", "asset", id];
export const REFRESH_AD_CREATIVE = (id: number, type: Channel) => [
  "list",
  "creative",
  id,
  type,
  "refresh",
];
export function useCreateAdCreative() {
  return useMutation(createCreative);
}
export function useListAdCreative({
  queryParams,
  enabled,
  type,
}: {
  type: Type;
  queryParams?: {
    page?: number;
    size?: number;
    adType?: number;
    isPagination: boolean;
    advertiserId?: number | null;
    isGrouped?: boolean;
  };
  enabled?: boolean;
}) {
  return useQuery<CreativeApiResponse>(
    LIST_AD_CREATIVE(
      type,
      queryParams?.advertiserId,
      queryParams?.page,
      queryParams?.size,
      queryParams?.adType,
      queryParams?.isGrouped
    ),
    getAssets,
    {
      enabled,
      meta: {
        queryParams: {
          ...queryParams,
          channel: type,
        },
      },
      select: filterDataAccordingToAllowedAdvertisers("advertiserId"),
    }
  );
}
export function useListInsightAudience({
  queryParams,
  enabled,
  type,
}: {
  type: Type;
  queryParams: {
    advertiserId?: number | null | undefined;
    executionType: any;
  };
  enabled: boolean;
}) {
  return useQuery(
    LIST_AD_CREATIVE(
      type,
      queryParams?.advertiserId,
      queryParams?.executionType
    ),
    getVisitorInsightAudience,
    {
      enabled,
      meta: {
        queryParams: {
          ...queryParams,
          channel: type,
          status: JSON.stringify(["completed", "processing"]),
        },
      },
      select: filterDataAccordingToAllowedAdvertisers("advertiserId"),
    }
  );
}
export function useListAdCreativeApprovals({
  queryParams,
  enabled,
  onSuccess,
  select,
}: {
  queryParams: {
    advertiserId?: number | null;
    creativeIds: number[];
  };
  onSuccess?: (data: any) => void;
  select?: (data: any) => any;
  enabled?: boolean;
}) {
  return useQuery(
    [
      "list",
      "creative",
      "approval",
      "status",
      queryParams?.advertiserId,
      ...(queryParams?.creativeIds ?? []),
    ],
    getCreativeApprovals,
    {
      enabled,
      onSuccess,
      select,
      meta: {
        queryParams,
      },
    }
  );
}
export function useDetailAdCreative({
  id,
  type,
  queryParams,
}: {
  id: number;
  type: "linkedin" | "display";
  queryParams?: any;
}) {
  return useQuery(LIST_AD_CREATIVE_DETAILS(id, type), getAssetDetails, {
    meta: {
      id,
      type,
      queryParams,
    },
    select: filterDataAccordingToAllowedAdvertisers("advertiserId"),
  });
}
export function useGetImageByUrn({
  id,
  queryParams,
}: {
  id: string;
  queryParams?: any;
}) {
  return useQuery(["creatives", "image", id], getImageByUrn, {
    meta: {
      id,
      queryParams,
    },
  });
}

export function useGetBwCreativeDetails({
  id,
  options,
}: {
  id: string;
  options?: ReactQueryOptions<unknown, unknown, unknown, string>;
}) {
  return useQuery(["creatives", "bw", id], getBwCreativeDetails, {
    meta: {
      id,
    },
    ...options,
  });
}

export function useFetchCreatives() {
  const queryClient = useQueryClient();
  async function fetchCreativeList(args: {
    channel: Channel;
    advertiserId: number;
    adType: number;
  }) {
    const { channel, advertiserId, adType } = args;
    const queryKey = LIST_AD_CREATIVE(
      channel,
      advertiserId,
      undefined,
      undefined,
      adType,
      true
    );
    let creativeList: CreativeApiResponse | undefined =
      queryClient.getQueryData(queryKey);

    if (!creativeList) {
      creativeList = await queryClient.fetchQuery({
        queryKey,
        queryFn: getAssets,
        meta: {
          queryParams: {
            channel,
            adType,
            advertiserId,
          },
        },
      });
    }
    return creativeList;
  }

  return {
    fetchCreativeList,
  };
}
export function useGetBwCreativeAsset({
  id,
  onSuccess,
}: {
  id: number;
  onSuccess?: (data: any) => void;
}) {
  return useQuery(AD_ASSET(id), getBwCreativeAsset, {
    onSuccess,
    meta: {
      id,
    },
  });
}

export function useRefreshAds() {
  return useMutation(refreshAds);
}
export function useDeleteAds() {
  return useMutation(deleteAd);
}
export function useCreateAssets() {
  return useMutation(createAssets);
}

export function useGetCreativeAddons({
  enabled,
  creativeType,
}: {
  enabled?: boolean;
  creativeType?: string;
}) {
  return useQuery(
    ["list", "creative", "addons", creativeType],
    getCreativeAddons,
    {
      enabled,
      meta: {
        queryParams: {
          creativeType,
        },
      },
    }
  );
}

export function useValidDimensions() {
  function checkValidDisplayAssetDimensions(
    dimention: string,
    creativeType: CreativeType
  ) {
    if (DISPLAY_ASSET_AD_SIZE[creativeType])
      return DISPLAY_ASSET_AD_SIZE[creativeType].includes(dimention);
    else return displayAdSizes.includes(dimention);
  }
  function checkValidLinkedinAssetDimentions({
    width,
    height,
  }: {
    width: number;
    height: number;
  }) {
    if (width === height) {
      return compareDientions({ width, height, type: "square" });
    }
    if (width > height) {
      return compareDientions({ width, height, type: "horizontal" });
    }
    if (width < height) {
      return compareDientions({ width, height, type: "vertical" });
    }
  }
  function compareDientions({
    width,
    height,
    type,
  }: {
    width: number;
    height: number;
    type: "horizontal" | "vertical" | "square";
  }) {
    return (
      width >= linkedinAdSizes[type].minWidth &&
      width <= linkedinAdSizes[type].maxWidth &&
      height >= linkedinAdSizes[type].minHeight &&
      height <= linkedinAdSizes[type].maxHeight
    );
  }
  return {
    checkValidDisplayAssetDimensions,
    checkValidLinkedinAssetDimentions,
    compareDientions,
  };
}

type ImageSource = {
  imageIsLoaded: boolean;
  loadedStatus: ImageLoadingState;
  image: any;
  isLoading: boolean;
  previewUrl?: string;
}[];

export function useThumbnail() {
  function handleImageSources(row: SingleCreative): ImageSource {
    const data = [];
    if (row?.channel === "facebook" && row?.thumbnailUrl) {
      data.push(loadImageFromUrl(row.thumbnailUrl));
    } else if (
      row?.channel === "display" &&
      !row?.CreativeTemplateDataValueMapping?.id
    ) {
      if (row?.adId) {
        data.push(loadBeeswaxImage(row?.adId));
      }
    } else if (row?.carouselCards) {
      row.carouselCards.forEach((item: any) => {
        data.push(loadLinkedinImage(item?.linkedinAssetUrn, row.advertiserId));
      });
    } else if (row?.linkedinAssetUrn) {
      data.push(loadLinkedinImage(row?.linkedinAssetUrn, row.advertiserId));
    }
    return data;
  }

  function loadLinkedinImage(asset: string, advertiserId: number) {
    const { data, isLoading: imageLoading } = useGetImageByUrn({
      id: asset,
      queryParams: {
        advertiserId,
      },
    });
    const loadedStatus = useImageLoaded({
      src: data?.downloadUrl,
      srcLoading: imageLoading,
    });
    const imageIsLoaded = Boolean(loadedStatus === "loaded");
    return {
      imageIsLoaded,
      loadedStatus,
      image: data?.downloadUrl,
      isLoading: imageLoading,
    };
  }

  function loadBeeswaxImage(assetId: string) {
    const { data: creativeDetails, isLoading: creativeDetailsLoading } =
      useGetBwCreativeDetails({
        id: assetId,
      });

    const loadedStatus = useImageLoaded({
      src: creativeDetails?.thumbnailUrl,
      srcLoading: creativeDetailsLoading,
    });
    const imageIsLoaded = Boolean(loadedStatus === "loaded");
    return {
      imageIsLoaded,
      loadedStatus,
      image: creativeDetails?.thumbnailUrl,
      previewUrl: creativeDetails?.previewUrl,
      isLoading: creativeDetailsLoading,
    };
  }
  function loadBeeswaxAsset(assetId: number): ILoadBWAsset {
    const {
      data: creativeAssetDetails,
      isLoading: creativeAssetDetailsLoading,
    } = useGetBwCreativeAsset({
      id: assetId,
    });

    const loadedStatus = useImageLoaded({
      src: creativeAssetDetails?.previewUrl,
      srcLoading: creativeAssetDetailsLoading,
    });
    const imageIsLoaded = Boolean(loadedStatus === "loaded");
    return {
      imageIsLoaded,
      loadedStatus,
      isLoading: creativeAssetDetailsLoading,
      ...creativeAssetDetails,
    };
  }

  function loadImageFromUrl(url: string) {
    const loadedStatus = useImageLoaded({
      src: url,
      srcLoading: false,
    });
    const imageIsLoaded = Boolean(loadedStatus === "loaded");
    return {
      imageIsLoaded,
      loadedStatus,
      image: url,
      isLoading: false,
    };
  }
  return { handleImageSources, loadBeeswaxImage, loadBeeswaxAsset };
}
export function useHandleSubmit() {
  const { setSnackbarState } = useSnackbarDispatch();
  const { checkValidDisplayAssetDimensions } = useValidDimensions();
  const { mutateAsync: createAsset } = useCreateAssets();
  const navigate = useNavigate();
  const location = useLocation();
  const queryKeys = location?.state.queryKeys;
  const queryClient = useQueryClient();

  async function handleSubmitCreative({
    data,
    setLoading,
    key,
    isEdit = false,
    creativeId,
    groupAdId,
    creative,
    setValue,
    onShowZipPreview,
  }: {
    data: any;
    setLoading: React.Dispatch<boolean>;
    key: "linkedin" | "display" | "facebook";
    isEdit?: boolean;
    creativeId?: number;
    groupAdId?: string;
    creative?: any;
    setValue?: UseFormSetValue<any>;
    onShowZipPreview?: ShowZipPreviewCallback;
  }) {
    try {
      setLoading(true);
      const { file, creativeType } = data;
      if (creativeType === CreativeType.CAROUSAL) {
        await createCarousalAd({
          isEdit,
          creative,
          setLoading,
          key,
          data,
          creativeId,
          groupAdId,
        });
      } else if (
        creativeType === CreativeType.IMAGE ||
        creativeType === CreativeType.VIDEO ||
        creativeType === CreativeType.NATIVE_IMAGE_CONTENT ||
        creativeType === CreativeType.TEXT
      ) {
        await uploadMedia({
          file,
          isEdit,
          creative,
          setLoading,
          key,
          data,
          creativeId,
          groupAdId,
          setValue,
        });
      } else if (
        creativeType === CreativeType.MULTI_IMAGE ||
        creativeType === CreativeType.MULTI_VIDEO
      ) {
        await uploadZip({
          key,
          isEdit,
          setLoading,
          file,
          data,
          creative,
          creativeId,
          groupAdId,
          onShowZipPreview,
        });
      } else if (creativeType === CreativeType.HTML) {
        await uploadHtml({
          key,
          isEdit,
          setLoading,
          file,
          data,
          creative,
          creativeId,
          groupAdId,
        });
      } else if (creativeType === CreativeType.JS_TAG) {
        await uploadJsTag({
          isEdit,
          creative,
          setLoading,
          key,
          data,
          creativeId,
          groupAdId,
        });
      } else if (creativeType === CreativeType.VAST) {
        await uploadVast({
          isEdit,
          creative,
          setLoading,
          key,
          data,
          creativeId,
          groupAdId,
        });
      } else {
        setSnackbarState({
          open: true,
          severity: "error",
          message: messages.invalidFileType,
        });
      }

      if (queryKeys) {
        await queryClient.refetchQueries(queryKeys);
      } else {
        queryClient.invalidateQueries(
          LIST_AD_CREATIVE(
            "ALL",
            data.advertiserId,
            undefined,
            undefined,
            undefined,
            true
          )
        );
      }
      if (creativeType === CreativeType.VIDEO)
        setSnackbarState({
          open: true,
          severity: "success",
          message:
            "Your video is being encoded in the background and will appear here once it's ready. This process may take a few minutes.",
        });
      navigate(`${routes.DASHBOARD}/${routes.ASSETS.CREATIVES.INDEX}`);
    } catch (error: any) {
      // User intentionally cancelled the ZIP preview dialog — not an error,
      // so don't surface an error snackbar.
      if (error?.message === UPLOAD_CANCELLED_ERROR) {
        return;
      }
      const isVideoTransCodeError =
        error?.errMsg?.includes(
          "Creative template includes macros not found in creative"
        ) && data?.creativeType === CreativeType.VIDEO;
      setSnackbarState({
        open: true,
        severity: "error",
        message: isVideoTransCodeError
          ? messages.videoAssetTranscoding
          : correctTypedErrorMessage(error),
      });
    } finally {
      setLoading(false);
    }
  }

  async function uploadMedia({
    file,
    isEdit,
    creative,
    setLoading,
    key,
    data,
    creativeId,
    groupAdId,
    setValue,
  }: any) {
    const isImage = file?.type?.startsWith("image/");
    const isVideo = file?.type?.startsWith("video/");
    const mimeType = file?.type?.split("/")[0];
    const fileName = file?.name && cleanFileName(file.name);
    if ((isEdit && file && !fileName) || (!isEdit && !fileName))
      throw new Error("The selected file must have a valid name");
    const {
      groupName,
      name,
      introductoryText,
      clickUrl = "",
      callToAction,
      advertiserId,
      heading,
      description,
      creativeType,
      creativeAddons,
      nativeOfferId,
      videoTitle,
      videoAdDuration,
      pixels,
      scripts,
      assetId,
      utmId,
    } = data;
    const payload: any = {
      name: name || fileName,
      assetId,
      groupName,
      groupAdId,
      clickUrl,
      advertiserId,
      nativeOfferId,
      creativeType,
      pixels,
      scripts,
      assetInfo: {
        ...creative?.assetInfo,
        videoTitle,
        videoAdDuration,
        ...(key === "display" && creativeAddons
          ? { creativeAddons }
          : undefined),
      },
      type:
        key === "display"
          ? creativeType === CreativeType.IMAGE
            ? ASSET_TYPES.BANNER
            : creativeType === CreativeType.NATIVE_IMAGE_CONTENT
            ? ASSET_TYPES.NATIVE
            : ASSET_TYPES.VIDEO
          : key === "linkedin"
          ? internalCreativeTypeToLinkedinCreativeTypeMapping[
              creativeType as AllowedCreativeType
            ]
          : null,
      utmId,
    };
    let dimentions: any = {};

    if (file) {
      if (key === "display" && !isVideo) {
        dimentions = await getDimensionsFromImageDataUrl(
          window.URL.createObjectURL(file)
        );

        const isValid = checkValidDisplayAssetDimensions(
          `${dimentions.width}x${dimentions.height}`,
          creativeType
        );
        if (!isValid) {
          return Promise.reject({
            errMsg: messages.invalidImageRatio,
          });
        }
      }

      payload.assetInfo = {
        ...payload?.assetInfo,
        fileSize: file?.size,
        fileName,
        type: file?.type,
        mimeType: mimeType,
        ...(isImage ? { dimentions } : {}),
        ...(isVideo ? { duration: await getVideoDuration(file) } : undefined),
      };
    }
    if (key === "linkedin") {
      if (callToAction) payload["callToActionButton"] = callToAction;
      payload["introductoryText"] = introductoryText;
      payload["description"] = description;
      payload["heading"] = heading;
      if (file) {
        const formData = new FormData();
        formData.append("image", file);
        formData.append("advertiserId", advertiserId);
        formData.append("isVideo", `${isVideo}`);
        formData.append("fileSize", `${file.size}`);
        const creativeAsset = await createAsset({
          data: formData,
          type: "linkedin",
        });
        payload["linkedinAssetUrn"] = isVideo
          ? creativeAsset.value.video
          : creativeAsset.value.image;
      }
    } else if (key === "display" && file && !payload.assetId) {
      const formData = new FormData();
      formData.append("file", file, fileName);
      formData.append("advertiserId", advertiserId);
      formData.append("click_url", clickUrl || "");
      const data = await createAsset({ data: formData, type: "display" });
      payload["assetId"] = data.id;
      if (creativeType === CreativeType.VIDEO) setValue?.("assetId", data.id);
    } else if (key === "facebook" && file) {
      payload["description"] = description;
      const formData = new FormData();
      if (isImage) {
        formData.append("advertiserId", advertiserId);
        const imageBytes = await convertImageToBase64(file);
        if (imageBytes) formData.append("imageBytes", imageBytes);
        else {
          setSnackbarState({
            open: true,
            severity: "error",
            message: messages.invalidFileType,
          });
          return;
        }
        const data = await createAsset({ data: formData, type: "facebook" });
        payload["imageHash"] = data.hash;
      } else if (isVideo) {
        formData.append("file", file, fileName);
        formData.append("advertiserId", advertiserId);
        const creativeAsset = await createAsset({
          data: formData,
          type: "facebook",
        });
        payload["videoId"] = creativeAsset.id;
      }
    }

    if (isEdit && creativeId) {
      await editCreative({
        payload,
        type: key,
        id: creativeId,
      });
    } else {
      await createCreative({
        payload,
        type: key,
      });
    }

    setSnackbarState({
      open: true,
      message: messages.createdSuccessfully("Asset"),
    });
  }
  async function createCarousalAd({
    file,
    isEdit,
    creative,
    setLoading,
    key,
    data,
    creativeId,
    groupAdId,
  }: any) {
    const {
      groupName,
      name,
      introductoryText,
      clickUrl = "",
      advertiserId,
      heading,
      cards = [],
      creativeType,
      utmId,
    } = data;
    cards.forEach((item: any) => {
      const isImage = item?.file?.type?.startsWith("image/");
      if (!isImage)
        setSnackbarState({
          open: true,
          severity: "error",
          message: "invalid",
        });
    });

    const uploadedSignedUrlsPromises: any[] = cards.map(
      (item: any) =>
        new Promise((resolve, reject) => {
          const formData = new FormData();
          formData.append("image", item.file);
          formData.append("advertiserId", advertiserId);
          formData.append("fileSize", `${item.file.size}`);
          createAsset({ data: formData, type: "linkedin" })
            .then(async (data) => {
              const dimentions = await getDimensionsFromImageDataUrl(
                window.URL.createObjectURL(item.file)
              );
              resolve({
                linkedinAssetUrn: data.value.image,
                mimeType: item?.file?.type?.split("/")[0],
                heading: item?.heading,
                clickUrl: item?.clickUrl,
                fileSize: item?.file.size,
                fileName: item?.file.name,
                type: item?.file.type,
                dimentions,
              });
            })
            .catch((error: any) => {
              console.log(error);
            });
        })
    );
    const uploadedSignedUrls = await Promise.all(uploadedSignedUrlsPromises);

    const payload: any = {
      name,
      groupName,
      groupAdId,
      clickUrl,
      advertiserId,
      type: internalCreativeTypeToLinkedinCreativeTypeMapping[
        data.creativeType as AllowedCreativeType
      ],
      carouselCards: uploadedSignedUrls,
      introductoryText,
      heading,
      utmId,
    };

    if (isEdit && creativeId) {
      await editCreative({
        payload,
        type: key,
        id: creativeId,
      });
    } else {
      await createCreative({
        payload,
        type: key,
      });
    }

    setSnackbarState({
      open: true,
      message: messages.createdSuccessfully("Asset"),
    });
  }
  async function uploadJsTag({
    isEdit,
    creative,
    setLoading,
    key,
    data,
    creativeId,
    groupAdId,
  }: any) {
    const {
      name,
      groupName,
      advertiserId,
      creativeType,
      dimensions,
      adServerMacros: creativeRuleKey,
      creativeContentTag,
      creativeAddons,
      pixels,
      scripts,
    } = data;
    const [width, height] = dimensions.split("x");

    const payload: any = {
      name,
      groupName,
      groupAdId,
      advertiserId,
      creativeType,
      pixels,
      scripts,
      type: isEdit ? creative.type : ASSET_TYPES.BANNER,
      creativeContentTag,
      creativeRuleKey,
      assetInfo: {
        dimentions: {
          width,
          height,
        },
        creativeAddons,
      },
    };
    if (isEdit && creativeId) {
      await editCreative({
        payload,
        type: key,
        id: creativeId,
      });
    } else {
      await createCreative({
        payload,
        type: key,
      });
    }

    setSnackbarState({
      open: true,
      message: messages.createdSuccessfully("Asset"),
    });
  }

  async function uploadVast({
    isEdit,
    creative,
    key,
    data,
    creativeId,
    groupAdId,
  }: any) {
    const {
      name,
      groupName,
      advertiserId,
      creativeType,
      vastTagUrl,
      creativeAddons,
      pixels,
      scripts,
    } = data;

    // No binary upload — the wrapper is just a 3rd-party VAST tag URL. Backend
    // extracts video attributes and validates the tag on submit, so a bad tag
    // surfaces as an error snackbar (see handleSubmitCreative catch). No click
    // URL: the Beeswax VAST/VPAID wrapper template rejects click_url (click
    // tracking is out of scope for VAST v1).
    const payload: any = {
      name,
      groupName,
      groupAdId,
      advertiserId,
      creativeType,
      vastTagUrl,
      pixels,
      scripts,
      // VAST persists as a "video" DisplayAd type (backend also derives this).
      type: isEdit ? creative.type : ASSET_TYPES.VIDEO,
      assetInfo: { creativeAddons },
    };
    if (isEdit && creativeId) {
      await editCreative({
        payload,
        type: key,
        id: creativeId,
      });
    } else {
      await createCreative({
        payload,
        type: key,
      });
    }

    setSnackbarState({
      open: true,
      message: messages.createdSuccessfully("Asset"),
    });
  }

  async function uploadZip({
    key,
    isEdit,
    setLoading,
    file,
    data,
    groupAdId,
    onShowZipPreview,
  }: any) {
    if (isEdit) throw new Error("This feature is not available for edit");
    const isZip = file?.type?.split("/")?.[1]?.includes("zip");
    if (!isZip) {
      throw new Error(messages.invalidFileType);
    }

    const fileName = file?.name && cleanFileName(file.name)?.split(".")[0];
    if (!fileName) throw new Error("The selected file must have a valid name");

    const {
      introductoryText,
      clickUrl = "",
      callToAction,
      advertiserId,
      heading,
      description,
      creativeType,
      name,
      groupName,
      videoTitle,
      videoAdDuration,
      pixels,
      scripts,
      utmId,
    } = data;

    let assetsInfo: ZipEntryAssetInfo[] | undefined;
    let fileToUpload: File = file;

    // For display channel, use the new categorized validation with preview
    if (key === "display") {
      const validationResult = await validateAndCategorizeAssetsForDisplay(
        file as File
      );

      // If no valid files, throw error immediately
      if (!validationResult.hasValidAssets) {
        throw new Error(
          "All files in the ZIP are invalid. Please check file formats, names, and dimensions."
        );
      }

      // If there are invalid files, show preview dialog for user confirmation
      if (validationResult.invalidAssets.length > 0 && onShowZipPreview) {
        // This will throw if user cancels
        await onShowZipPreview(validationResult);
      }

      // Always create clean ZIP to ensure filenames are sanitized
      // (e.g. leading underscores/hyphens stripped by cleanFileName)
      fileToUpload = await createCleanZipFromValidEntries(
        validationResult.validEntries,
        file.name
      );

      assetsInfo = validationResult.validAssets;
    } else if (key === "linkedin") {
      // LinkedIn uses the original validation
      const validateAsset = validateAssetFunctionByChannel(key);
      if (!validateAsset) throw new Error("No Valid Asset Function Found");
      const {
        isError,
        message,
        assetsInfo: linkedinAssetsInfo,
      } = await validateAsset(file as File);

      if (isError) {
        return Promise.reject({
          errMsg: message,
        });
      }
      assetsInfo = linkedinAssetsInfo;
    }

    const payload: any = {
      groupName,
      groupAdId,
      name,
      clickUrl,
      advertiserId,
      creativeType,
      pixels,
      scripts,
      assetInfo: {
        videoTitle,
        videoAdDuration,
      },
      type:
        key === "display"
          ? creativeType === CreativeType.MULTI_IMAGE
            ? ASSET_TYPES.BANNER
            : ASSET_TYPES.VIDEO
          : key === "linkedin"
          ? internalCreativeTypeToLinkedinCreativeTypeMapping[
              creativeType as AllowedCreativeType
            ]
          : null,
      utmId,
    };
    let bulkManifest: any = [];

    if (key === "linkedin") {
      payload["callToActionButton"] = callToAction;
      payload["introductoryText"] = introductoryText;
      payload["description"] = description;
      payload["heading"] = heading;
      const fileListResponse = await createBulkLinkedinAssets(
        file,
        advertiserId,
        createAsset
      );
      bulkManifest = fileListResponse;
    } else if (key === "display") {
      const formData = new FormData();
      // Use the clean ZIP file (or original if no invalid files)
      formData.append("file", fileToUpload, fileName);
      formData.append("advertiserId", advertiserId);
      formData.append("click_url", clickUrl || "");
      const data = await createAsset({ data: formData, type: "display" });
      payload["bulkAssetId"] = data.id;
      bulkManifest = data.manifest;
    }

    let hadPartialFailures = false;
    if (key === "display") {
      hadPartialFailures = await uploadBulkToDisplay({
        bulkManifest,
        file: fileToUpload,
        key,
        payload,
        assetsInfo,
      });
    } else if (key === "linkedin") {
      await uploadBulkToLinkedin({ bulkManifest, file, key, payload });
    }

    // On partial success the per-asset toasts already report the accurate
    // outcome, so skip the generic "created successfully" message.
    if (!hadPartialFailures) {
      setSnackbarState({
        open: true,
        message: messages.createdSuccessfully("Asset"),
      });
    }
  }

  async function uploadHtml({
    file,
    isEdit,
    creative,
    setLoading,
    key,
    data,
    creativeId,
    groupAdId,
  }: any) {
    const isHtml = file?.type?.endsWith("html");
    const mimeType = file?.type?.split("/")[0];
    if (file && !isHtml) {
      return Promise.reject({
        errMsg: messages.invalidFileType,
      });
    }
    const fileName = file?.name && cleanFileName(file.name);

    const {
      name,
      groupName,
      clickUrl = "",
      advertiserId,
      creativeType,
      creativeAddons,
      dimensions,
      pixels,
      scripts,
    } = data;
    const [width, height] = dimensions.split("x");
    const payload: any = {
      name: name || fileName,
      groupName,
      groupAdId,
      clickUrl,
      advertiserId,
      creativeType,
      pixels,
      scripts,
      type: isEdit ? creative.type : ASSET_TYPES.BANNER,
      assetInfo: {
        ...creative?.assetInfo,
        dimentions: {
          width,
          height,
        },
        ...(key === "display" ? { creativeAddons } : undefined),
      },
    };
    if (file) {
      payload.assetInfo = {
        ...payload.assetInfo,
        fileSize: file?.size,
        fileName,
        type: file?.type,
        mimeType,
      };
      if (key === "display") {
        const formData = new FormData();
        formData.append("file", file, fileName);
        formData.append("advertiserId", advertiserId);
        formData.append("click_url", clickUrl || "");
        const data = await createAsset({ data: formData, type: "display" });
        payload["assetId"] = data.id;
      }
    }

    if (isEdit && creativeId) {
      await editCreative({
        payload,
        type: key,
        id: creativeId,
      });
    } else
      await createCreative({
        payload,
        type: key,
      });
    setSnackbarState({
      open: true,
      message: messages.createdSuccessfully("Asset"),
    });
  }

  async function uploadBulkToDisplay({
    bulkManifest,
    file,
    key,
    payload,
    assetsInfo,
  }: {
    bulkManifest: any;
    file: any;
    key: string;
    payload: any;
    assetsInfo: ZipEntryAssetInfo[] | undefined;
  }) {
    if (!assetsInfo) {
      throw new Error(" Assets info not found");
    }

    const promiseList = [];
    const errors: {
      [key: string]: number;
    } = {};
    const tally = (key: string) => {
      errors[key] = (errors[key] || 0) + 1;
    };
    const groupAdId = payload?.groupAdId ?? uuidv4();
    for (let i = 0; i < bulkManifest.length; i++) {
      const manifest = bulkManifest[i];
      if (!manifest.success) {
        // Fall back to a generic key so a failed entry without an error
        // message still gets counted instead of being silently dropped.
        const errorMessage = manifest?.error?.[0] || "Unknown error";
        tally(errorMessage);
        continue;
      }
      const creativeName = manifest.creative_name;
      const assetInfo = assetsInfo.find(
        (assetInfo) => assetInfo.name === creativeName
      );
      if (!assetInfo) {
        tally("No asset info found");
        continue;
      }
      const requiredPayload = {
        ...payload,
        adId: manifest.id,
        assetId: manifest.id,
        type: ASSET_TYPES.BANNER,
        name: manifest.creative_name,
        assetInfo: {
          ...payload?.assetInfo,
          mimeType:
            payload.creativeType === CreativeType.VIDEO ? "video" : "image",
          dimentions: assetInfo.dimensions,
          type: file?.type,
          fileSize: assetInfo.fileSize,
          fileName: assetInfo.name,
        },
        groupAdId,
      };
      promiseList.push(
        createCreative({
          payload: requiredPayload,
          type: key,
        })
      );
    }
    // Wait for ALL creative creations without aborting siblings on the first
    // failure. Promise.all would reject on the first rejection and hide the
    // creatives that already succeeded, leaving the user with a generic error
    // even though some assets were created (and inviting a duplicate retry).
    const results = await Promise.allSettled(promiseList);

    // Fold per-creative (step 2) failures into the same tally as the manifest
    // (step 1) failures so they're reported rather than silently swallowed.
    results.forEach((result) => {
      if (result.status === "rejected") {
        const reason: any = result.reason;
        const message =
          reason?.errMsg || reason?.message || "Failed to create creative";
        tally(message);
      }
    });

    const succeededCount = results.filter(
      (result) => result.status === "fulfilled"
    ).length;
    const errorKeys = Object.keys(errors);

    // Nothing was created at all -> surface as a hard error (caller shows the
    // error and stays on the form). Distinguish "everything we tried failed"
    // from "the upload produced nothing to create" so the message isn't
    // misleading when the manifest came back empty.
    if (succeededCount === 0) {
      throw new Error(
        errorKeys.length > 0
          ? "All assets failed to upload"
          : "No assets were found in the uploaded file"
      );
    }

    // Partial success: report accurate counts and return normally so the
    // caller navigates to the list — the user can see what WAS created instead
    // of being told everything failed.
    const hadFailures = errorKeys.length > 0;
    if (hadFailures) {
      enqueueSnackbar(`${succeededCount} asset succeeded`, {
        severity: "success",
      });
      errorKeys.forEach((error) => {
        const splittedMessage = error.split("ERROR:");
        const message =
          splittedMessage.length > 1 ? splittedMessage[1] : splittedMessage[0];
        enqueueSnackbar(`${errors[error]} asset failed: ${message}`, {
          severity: "error",
        });
      });
    }

    // Signal partial failure so the caller can skip the generic success toast.
    return hadFailures;
  }

  async function uploadBulkToLinkedin({
    key,
    file,
    bulkManifest,
    payload,
  }: any) {
    const promiseList = [];
    const groupAdId = payload?.groupAdId ?? uuidv4();
    for (let i = 0; i < bulkManifest?.assetsPromiseList?.length; i++) {
      const zipEntryInfo = await getAssetInfo(
        key,
        file,
        bulkManifest.assetNames[i]
      );
      const creativePayload = {
        ...payload,
        linkedinAssetUrn:
          bulkManifest.assetsPromiseList[i].value.image ||
          bulkManifest.assetsPromiseList[i].value.video,
        name: zipEntryInfo.fileName,
        assetInfo: {
          mimeType: bulkManifest.assetsPromiseList[i].value.image
            ? "image"
            : "video",
          dimentions: zipEntryInfo.dimentions,
          fileSize: zipEntryInfo.fileSize,
          fileName: zipEntryInfo.fileName,
          duration: zipEntryInfo.duration,
        },
        groupAdId,
      };
      promiseList.push(
        createCreative({
          payload: creativePayload,
          type: key,
        })
      );
    }
    await Promise.all(promiseList);
  }

  return {
    handleSubmitCreative,
  };
}
